import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  TextField,
  useTheme,
  alpha,
  IconButton,
  Button,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import {
  Search,
  Mic,
  Square,
  Copy,
  Check,
  MoveHorizontal,
  Volume2,
  Keyboard,
  Globe,
} from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config';
import {
  ProgressModal,
  FeedbackSnackbar,
  uploadToScreenApp,
  UploadState,
  CopyState,
  useIframeResizer,
  GEMINI_MODEL,
} from './common';

interface LiveTranslatorState extends UploadState, CopyState {
  isRecording: boolean;
  inputText: string;
  translatedText: string;
  inputLanguage: string;
  outputLanguage: string;
  copyInputSuccess: boolean;
  copyOutputSuccess: boolean;
}

const LANGUAGES = [
  // Most Used Languages (High engagement)
  { code: 'en', name: 'English', group: 'Popular' },
  { code: 'hi', name: 'Hindi', group: 'Popular' },
  { code: 'ms', name: 'Malay', group: 'Popular' },
  { code: 'ar', name: 'Arabic', group: 'Popular' },
  { code: 'ur', name: 'Urdu', group: 'Popular' },
  { code: 'bn', name: 'Bengali', group: 'Popular' },
  { code: 'ta', name: 'Tamil', group: 'Popular' },
  { code: 'id', name: 'Indonesian', group: 'Popular' },

  // South & East Asian
  { code: 'zh', name: 'Chinese (Simplified)', group: 'Asian' },
  { code: 'ja', name: 'Japanese', group: 'Asian' },
  { code: 'ko', name: 'Korean', group: 'Asian' },
  { code: 'th', name: 'Thai', group: 'Asian' },
  { code: 'vi', name: 'Vietnamese', group: 'Asian' },
  { code: 'my', name: 'Burmese', group: 'Asian' },
  { code: 'km', name: 'Khmer', group: 'Asian' },
  { code: 'si', name: 'Sinhala', group: 'Asian' },
  { code: 'tl', name: 'Filipino', group: 'Asian' },

  // European (High engagement)
  { code: 'de', name: 'German', group: 'European' },
  { code: 'fr', name: 'French', group: 'European' },
  { code: 'it', name: 'Italian', group: 'European' },
  { code: 'es', name: 'Spanish', group: 'European' },
  { code: 'nl', name: 'Dutch', group: 'European' },
  { code: 'pl', name: 'Polish', group: 'European' },
  { code: 'ro', name: 'Romanian', group: 'European' },
  { code: 'ru', name: 'Russian', group: 'European' },
  { code: 'tr', name: 'Turkish', group: 'European' },
  { code: 'el', name: 'Greek', group: 'European' },
  { code: 'pt', name: 'Portuguese', group: 'European' },

  // Middle Eastern
  { code: 'fa', name: 'Persian', group: 'Middle Eastern' },
  { code: 'he', name: 'Hebrew', group: 'Middle Eastern' },
  { code: 'ku', name: 'Kurdish', group: 'Middle Eastern' },

  // African
  { code: 'sw', name: 'Swahili', group: 'African' },
  { code: 'am', name: 'Amharic', group: 'African' },
  { code: 'ha', name: 'Hausa', group: 'African' },
  { code: 'zu', name: 'Zulu', group: 'African' },

  // Nordic
  { code: 'sv', name: 'Swedish', group: 'Nordic' },
  { code: 'da', name: 'Danish', group: 'Nordic' },
  { code: 'fi', name: 'Finnish', group: 'Nordic' },
  { code: 'no', name: 'Norwegian', group: 'Nordic' },
  { code: 'is', name: 'Icelandic', group: 'Nordic' },
];

declare global {
  interface Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
  }
}

const LiveTranslator: React.FC = () => {
  useIframeResizer();
  const theme = useTheme();
  const { t } = useTranslation();

  const [state, setState] = useState<LiveTranslatorState>({
    isRecording: false,
    inputText: '',
    translatedText: '',
    inputLanguage: 'en',
    outputLanguage: 'es',
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    copySuccess: false,
    copyInputSuccess: false,
    copyOutputSuccess: false,
  });

  const recognition = useRef<any>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Group languages and filter based on search
  const groupedLanguages = LANGUAGES.reduce((acc, lang) => {
    if (!lang.name.toLowerCase().includes(searchQuery.toLowerCase()))
      return acc;

    if (!acc[lang.group]) {
      acc[lang.group] = [];
    }
    acc[lang.group].push(lang);
    return acc;
  }, {} as Record<string, typeof LANGUAGES>);

  const translateText = async (text: string) => {
    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `Translate the following text from ${
                    LANGUAGES.find((l) => l.code === state.inputLanguage)
                      ?.name || 'auto-detected language'
                  } to ${
                    LANGUAGES.find((l) => l.code === state.outputLanguage)?.name
                  }. Return ONLY the translated text, with no additional text, quotes, or formatting:

${text}`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(t('liveTranslator.messages.error'));
      }

      const data = await response.json();
      if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
        throw new Error('Invalid response format from API');
      }

      const translatedText = data.candidates[0].content.parts[0].text.trim();
      setState((prev) => ({ ...prev, translatedText }));
    } catch (error) {
      console.error('Translation error:', error);
      setState((prev) => ({
        ...prev,
        error: t('liveTranslator.messages.error'),
      }));
    }
  };

  useEffect(() => {
    // Initialize speech recognition
    if (!recognition.current) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognition.current = new SpeechRecognition();
        recognition.current.continuous = true;
        recognition.current.interimResults = true;

        recognition.current.onresult = (event: any) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = 0; i < event.results.length; i += 1) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }

          const newText = finalTranscript + interimTranscript;
          setState((prev) => ({ ...prev, inputText: newText }));
          translateText(newText);
        };

        recognition.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setState((prev) => ({
            ...prev,
            error: `Speech recognition error: ${event.error}`,
            isRecording: false,
          }));
        };

        recognition.current.onend = () => {
          console.log('Speech recognition ended');
          setState((prev) => ({ ...prev, isRecording: false }));
          if (mediaRecorder.current?.state !== 'inactive') {
            mediaRecorder.current?.stop();
          }
        };
      }
    }
  }, []);

  useEffect(() => {
    if (state.inputText) {
      translateText(state.inputText);
    }
  }, [state.outputLanguage]);

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      mediaRecorder.current.stop();
      recognition.current?.stop();
      setState((prev) => ({ ...prev, isRecording: false }));

      mediaRecorder.current.stream.getTracks().forEach((track) => track.stop());
    }
  };

  const startRecording = async () => {
    try {
      console.log('Starting recording process...');
      setState((prev) => ({ ...prev, error: null, inputText: '' }));

      // Check if browser supports required APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t('liveTranslator.messages.browserError'));
      }

      if (!recognition.current) {
        throw new Error(t('liveTranslator.messages.recognitionError'));
      }

      // iOS specific constraints
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
        },
      };

      console.log(
        'Requesting microphone access with iOS optimized settings...'
      );

      // For iOS Safari, we need to handle the stream directly
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Microphone access granted:', stream.active);

      if (!stream.active) {
        throw new Error(t('liveTranslator.messages.microphoneError'));
      }

      // Force audio context to start (required for iOS)
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioContext.createMediaStreamSource(stream); // Create source but don't need to store it
      await audioContext.resume();
      console.log('Audio context started:', audioContext.state);

      // Check for iOS-compatible recording format
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm';
      console.log('Using mime type:', mimeType);

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      console.log('MediaRecorder created:', mediaRecorder.current.state);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        console.log('Recording stopped, processing audio...');
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        uploadToScreenApp(
          audioBlob,
          'translation.webm',
          () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
          (progress) => setState((prev) => ({ ...prev, progress })),
          () => setState((prev) => ({ ...prev, uploadComplete: true })),
          (error) => setState((prev) => ({ ...prev, error, uploading: false }))
        );
      };

      mediaRecorder.current.onerror = (event: Event) => {
        console.error('MediaRecorder error:', event);
        setState((prev) => ({
          ...prev,
          error: 'Recording error occurred. Please try again.',
          isRecording: false,
        }));
      };

      console.log('Starting MediaRecorder...');
      mediaRecorder.current.start();

      console.log('Setting recognition language:', state.inputLanguage);
      recognition.current.lang = state.inputLanguage;
      recognition.current.start();
      setState((prev) => ({ ...prev, isRecording: true }));
      console.log('Recording started successfully');
    } catch (error) {
      console.error('Recording setup error:', error);
      setState((prev) => ({
        ...prev,
        error: t('liveTranslator.messages.error'),
        isRecording: false,
      }));
    }
  };

  const copyToClipboard = async (text: string, type: 'input' | 'output') => {
    try {
      await navigator.clipboard.writeText(text);
      setState((prev) => ({
        ...prev,
        [type === 'input' ? 'copyInputSuccess' : 'copyOutputSuccess']: true,
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          [type === 'input' ? 'copyInputSuccess' : 'copyOutputSuccess']: false,
        }));
      }, 2000);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: t('liveTranslator.messages.copyError'),
      }));
    }
  };

  const swapLanguages = () => {
    setState((prev) => ({
      ...prev,
      inputLanguage: prev.outputLanguage,
      outputLanguage: prev.inputLanguage,
      inputText: prev.translatedText,
      translatedText: prev.inputText,
    }));
  };

  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        fontFamily: 'Inter',
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="error"
            onClick={state.isRecording ? stopRecording : startRecording}
            startIcon={state.isRecording ? <Square /> : <Mic />}
            size="large"
            sx={{
              minWidth: 140,
              px: 3,
              borderRadius: 28,
              py: 2,
              fontSize: '1.2rem',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: '#d32f2f',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            {state.isRecording
              ? t('liveTranslator.controls.stop')
              : t('liveTranslator.controls.start')}
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              p: 1,
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FormControl fullWidth size="small" sx={{ minWidth: { sm: 150 } }}>
              <Select
                value={state.inputLanguage}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    inputLanguage: e.target.value,
                  }))
                }
                variant="standard"
                sx={{
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        minHeight: 36,
                        px: 2,
                      },
                    },
                  },
                }}
              >
                <ListSubheader
                  sx={{
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder={t('liveTranslator.controls.searchLanguages')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      my: 1,
                      '& .MuiInputBase-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>
                {Object.entries(groupedLanguages).map(([group, languages]) => [
                  <ListSubheader
                    key={group}
                    sx={{ bgcolor: 'background.paper', lineHeight: '32px' }}
                  >
                    {group}
                  </ListSubheader>,
                  ...languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      <Globe sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} />
                      {lang.name}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                my: { xs: 1, sm: 0 },
              }}
            >
              <IconButton
                onClick={swapLanguages}
                size="small"
                sx={{
                  p: 1,
                  transform: { xs: 'rotate(90deg)', sm: 'rotate(0deg)' },
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <MoveHorizontal />
              </IconButton>
            </Box>

            <FormControl fullWidth size="small" sx={{ minWidth: { sm: 150 } }}>
              <Select
                value={state.outputLanguage}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    outputLanguage: e.target.value,
                  }))
                }
                variant="standard"
                sx={{
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 400,
                      '& .MuiMenuItem-root': {
                        minHeight: 36,
                        px: 2,
                      },
                    },
                  },
                }}
              >
                <ListSubheader
                  sx={{
                    bgcolor: 'background.paper',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder={t('liveTranslator.controls.searchLanguages')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: '100%',
                      my: 1,
                      '& .MuiInputBase-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </ListSubheader>
                {Object.entries(groupedLanguages).map(([group, languages]) => [
                  <ListSubheader
                    key={group}
                    sx={{ bgcolor: 'background.paper', lineHeight: '32px' }}
                  >
                    {group}
                  </ListSubheader>,
                  ...languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      <Globe sx={{ fontSize: 16, mr: 1, opacity: 0.7 }} />
                      {lang.name}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small">
                  <Keyboard />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(state.inputText, 'input')}
                  sx={{ opacity: state.inputText ? 1 : 0.5 }}
                >
                  {state.copyInputSuccess ? (
                    <Check color="success" />
                  ) : (
                    <Copy />
                  )}
                </IconButton>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={state.inputText}
              onChange={(e) => {
                setState((prev) => ({ ...prev, inputText: e.target.value }));
                translateText(e.target.value);
              }}
              placeholder={
                state.isRecording
                  ? t('liveTranslator.messages.listening')
                  : t('liveTranslator.messages.enterText')
              }
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  lineHeight: 1.5,
                },
              }}
            />
          </Box>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small">
                  <Volume2 />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    copyToClipboard(state.translatedText, 'output')
                  }
                  sx={{ opacity: state.translatedText ? 1 : 0.5 }}
                >
                  {state.copyOutputSuccess ? (
                    <Check color="success" />
                  ) : (
                    <Copy />
                  )}
                </IconButton>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={state.translatedText}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
                sx: {
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  lineHeight: 1.5,
                },
              }}
              placeholder={t('liveTranslator.messages.translating')}
              variant="standard"
            />
          </Box>
        </Paper>

        <ProgressModal
          open={state.uploading}
          progress={state.progress}
          isComplete={state.uploadComplete}
          processingMessage={t('liveTranslator.messages.processing')}
          completeMessage={t('liveTranslator.messages.complete')}
        />

        <FeedbackSnackbar
          open={!!state.error}
          message={state.error || t('liveTranslator.messages.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, error: null }))}
        />
      </Box>
    </Box>
  );
};

export default LiveTranslator;
